import React, { useRef, useState, useEffect, ChangeEvent, KeyboardEvent, ClipboardEvent } from 'react';
import { Form } from 'antd';
import { SFields } from './SmsCodeForm.styles';
import { ISmsCodeInputProps } from './SmsCodeForm.types';

const SmsCodeInputComponent: React.FC<ISmsCodeInputProps & { value?: string; onChange?: (value: string) => void; }> = ({ 
  value = '', 
  name, 
  onChange, 
  size = 5, 
  separatorsAfter = [] 
}) => {
  const [values, setValues] = useState<string[]>(() => {
    const valArr = value?.split?.('') ?? [];
    const emptySize = size - valArr.length;
    return [...valArr, ...new Array(emptySize).fill('')];
  });
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // @ts-ignore
  const fieldStatus = Form.Item.useStatus();
  const isError = fieldStatus?.status === 'error';

  useEffect(() => {
    const valArr = value?.split?.('') ?? [];
    const emptySize = size - valArr.length;
    const newValues = [...valArr, ...new Array(emptySize).fill('')];
    setValues(newValues);
  }, [value, size]);

  useEffect(() => {
    const code = values.join('');
    onChange?.(code);
  }, [values, onChange]);

  const focusInput = (index: number) => {
    if (inputRefs.current[index]) {
      inputRefs.current[index]?.focus();
    }
  };

  const handleChange = (index: number, newValue: string) => {
    const digit = newValue.replace(/\D/g, '').slice(-1);
    
    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);

    if (digit && index < size - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, evt: KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Backspace') {
      evt.preventDefault();
      const newValues = [...values];
      
      if (values[index]) {
        newValues[index] = '';
        setValues(newValues);
      } else if (index > 0) {
        newValues[index - 1] = '';
        setValues(newValues);
        focusInput(index - 1);
      }
    } else if (evt.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    } else if (evt.key === 'ArrowRight' && index < size - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (evt: ClipboardEvent<HTMLInputElement>) => {
    evt.preventDefault();
    
    const pastedText = evt.clipboardData.getData('text').replace(/\D/g, '');
    
    if (!pastedText) return;

    const target = evt.target as HTMLInputElement;
    const startIndex = parseInt(target.dataset.index || '0');
    
    const newValues = [...values];
    const pastedChars = pastedText.split('').slice(0, size - startIndex);
    
    pastedChars.forEach((char, i) => {
      const index = startIndex + i;
      if (index < size) {
        newValues[index] = char;
      }
    });
    
    setValues(newValues);
    
    const nextIndex = Math.min(startIndex + pastedChars.length, size - 1);
    setTimeout(() => focusInput(nextIndex), 0);
  };

  return (
    <SFields ref={null}>
      {values.map((val, index) => (
        <React.Fragment key={index}>
          <input
            ref={el => inputRefs.current[index] = el}
            data-index={index}
            id={`${name}-num-${index}`}
            data-testid={`${name}-num-${index}`}
            value={val}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            maxLength={1}
            style={{
              width: '50px',
              height: '50px',
              textAlign: 'center',
              fontSize: '24px',
              background: 'transparent',
              border: 'none',
              borderBottom: isError ? '1px solid red' : '1px solid #444',
              margin: '0 4px',
              outline: 'none',
            }}
            type="tel"
            inputMode="numeric"
          />
          {separatorsAfter?.includes(index) && <span style={{ margin: '0 8px' }}>-</span>}
        </React.Fragment>
      ))}
    </SFields>
  );
};

export const SmsCodeInput: React.FC<ISmsCodeInputProps> = (props) => {
  return (
    <Form.Item name={props.name} trigger="onChange" valuePropName="value">
      <SmsCodeInputComponent {...props} />
    </Form.Item>
  );
}; 